import React, { useState, useEffect } from 'react'
import { all_routes } from '../../../router/all_routes'
import { Link } from 'react-router-dom'
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from '../../../../core/common/collapse-header/collapse-header';
import { API } from '../../../../common/Constants';
import useFetch from '../../../../hooks/useFetch';
import { getAPIHeaders, getFormattedDateDDMMYYYY, isBeforeCurrentDate, isCurrentDateInBetween } from '../../../../common/Utils';
import PreLoader from '../../../../core/common/preLoader';
import AddCurriculum from './curriculumAdd';
import ModifyCurriculum from './curriculumModify';
import { useNavigate } from "react-router"
import CopyRightInfo from '../../../../core/common/copyRightInfo';
import { useAccess } from '../../../../hooks/accessContext';
import DeleteModal from '../../../../core/modals/deleteModal';
import CustomToast from '../../../../core/common/customToast';
import "../../../../style/css/shared.scss";
import syllabus from '../syllabus/syllabus';

// Helper function to calculate curriculum completion percentage
const calculateCompletionPercentage = (startDate: string, endDate: string): { percentage: number, status: string } => {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const current = new Date().getTime();
  
  // If curriculum hasn't started yet
  if (current < start) {
    return { percentage: 0, status: 'upcoming' };
  }
  
  // If curriculum is already complete
  if (current > end) {
    return { percentage: 100, status: 'completed' };
  }
  
  // Calculate percentage of completion
  const totalDuration = end - start;
  const elapsedDuration = current - start;
  const percentage = Math.min(Math.round((elapsedDuration / totalDuration) * 100), 100);
  
  return { percentage, status: 'in-progress' };
};

const Curriculum = () => {
  const {hasAcademicMgntCreate, hasAcademicMgntDelete, hasAcademicMgntEdit} = useAccess()
  const navigation = useNavigate();
  const { isLoading, data, callFetch } = useFetch();
  const [curriculums, setCurriclums] = useState<any[]>([]);
  const [filteredCurriculums, setFilteredCurriculums] = useState<any[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [editId, setEditId] = useState<number>(0);
  const [deleteItem, setDeleteItem] = useState<any>({
            id:undefined, 
            message: undefined,
  })
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  const [viewType, setViewType] = useState<'table' | 'card'>('table');
  const [totalCurriculum, setTotalCurriculum] = useState(0);
  const [activeCurriculum, setActiveCurriculum] = useState(0);
  const [inactiveCurriculum, setInactiveCurriculum] = useState(0);
  const [upcomingCurriculum, setUpcomingCurriculum] = useState(0);

  useEffect(() => {
    fetch()
  }, []);

  useEffect(() => {
    if(data){
      setCurriclums(data);
      setFilteredCurriculums(data);
      
      // Calculate statistics for stat cards
      setTotalCurriculum(data.length);
      
      // Calculate counts for each status
      const active = data.filter((item: any) => item.status === 'ACTIVE').length;
      const inactive = data.filter((item: any) => item.status === 'INACTIVE').length;
      const upcoming = data.filter((item: any) => item.status === 'UPCOMING').length;
      
      setActiveCurriculum(active);
      setInactiveCurriculum(inactive);
      setUpcomingCurriculum(upcoming);
      
    }
  }, [data]);

  // Apply filter when activeFilter changes
  useEffect(() => {
    if (data) {
       if (activeFilter === 'active') {
        setFilteredCurriculums(data.filter((item: any) => item.status === 'ACTIVE'));
      } else if (activeFilter === 'inactive') {
        setFilteredCurriculums(data.filter((item: any) => item.status === 'INACTIVE'));
      } else if (activeFilter === 'upcoming') {
        setFilteredCurriculums(data.filter((item: any) => item.status === 'UPCOMING'));
      } else {
        setFilteredCurriculums(data);
      }
    }
  }, [activeFilter, data]);

  const fetch = () => {
    callFetch(API.CURRICULUM,
      'GET',
      getAPIHeaders(),
      null,
      null
  ).catch((err) => {
    setToastSettings((prev) => ({
      ...prev,
      show: true,
      message: err?.response?.data || "Something went wrong, please try again later.",
    }));
  });
  }

  const handleFilterClick = (filter: string) => {
    if (activeFilter === filter) {
      setActiveFilter(null); // Clear filter if clicked again
    } else {
      setActiveFilter(filter);
    }
  };

  const deleteData = (id: number) => {
    callFetch(`${API.CURRICULUM}/${id}`,
      'DELETE',
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      fetch()
    }).catch((error)=>{
      setToastSettings((prev)=>({    
        ...prev,
        show: true,
        message: error?.response?.data || "Something went wrong, please try again later.",
      }));
    });
  }

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      render: (text: String, record: any) => (
        <h6 className="fw-medium text-center">
          {text}
        </h6>

      ),
      sorter: (a: any, b: any) => a.id - b.id,
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-sm me-2 flex-shrink-0 bg-light-primary rounded-circle">
            <span className="text-primary fw-bold">
              {record.title.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h6 className="mb-0 fw-semibold curriculum-title">
              <Link 
                to="#" 
                className="text-reset text-decoration-none"
                onClick={() => { setEditId(record?.id) }}
                data-bs-toggle="modal"
                data-bs-target="#edit_curriculum"
              >
                {record.title}
              </Link>
            </h6>
            <div className="d-flex align-items-center mt-1 curriculum-info">
              <span className="badge badge-soft-primary me-2">{record.code}</span>
              {record.description && (
                <span className="fs-12 text-muted description-content text-truncate" 
                      style={{ maxWidth: '250px' }} 
                      title={record.description}>
                  {record.description}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.title?.localeCompare(b.title),
      width: "32%",
    },
    {
      title: "Courses",
      dataIndex: "",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center flex-column">
          {record?.courses?.length ? record?.courses?.map((course:any) => (
            <div key={course.code}>
              <h6 className="mb-1 fw-medium syllabus-title">
                <Link 
                  to={`/academics/courses?searchText=${course?.code}`}
                  className="text-reset text-decoration-none"
                  style={{ cursor: 'pointer' }}
                >
                  {course?.title}
                  {' '}
                  <span className="badge badge-soft-info me-2">{course?.code}</span>
                </Link>
              </h6>
            </div>
          )) : (
            <span className="text-muted">No courses assigned</span>
          )}
        </div>
      ),
      sorter: (a: any, b: any) => {
        const titleA = a?.syllabus && a?.syllabus[0] ? a.syllabus[0].title : '';
        const titleB = b?.syllabus && b?.syllabus[0] ? b.syllabus[0].title : '';
        return titleA.localeCompare(titleB);
      },
      width: "42%",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center justify-content-center">
          {
            !isCurrentDateInBetween(record.startDate,record.endDate) || text === 'INACTIVE' ?
            <span className="badge badge-soft-danger d-inline-flex align-items-center badge-sm">
              <i className="ti ti-circle-filled fs-8 me-1"></i> INACTIVE
            </span>
            :
            text === 'ACTIVE' ?
            <span className="badge badge-soft-success d-inline-flex align-items-center badge-sm">
              <i className="ti ti-circle-filled fs-8 me-1"></i> {text}
            </span>
            :
            <span className="badge badge-soft-info d-inline-flex align-items-center badge-sm">
              <i className="ti ti-circle-filled fs-8 me-1"></i> {text}
            </span>
          }
        </div>
      ),
      sorter: (a: any, b: any) => {
        const statusOrder: { [key: string]: number } = {
          ACTIVE: 1,
          UPCOMING: 2,
          INACTIVE: 3,
        };
        return statusOrder[a.status] - statusOrder[b.status];
      },
      defaultSortOrder: 'ascend',
      width: "15%",
      align: "center" as "center",
    },
    {
      title: "Schedule",
      dataIndex: "startDate",
      render: (text: String, record: any) => {
        const { percentage, status } = calculateCompletionPercentage(record.startDate, record.endDate);
        
        // Determine progress bar color based on status
        let progressColor = '';
        if (status === 'upcoming') {
          progressColor = 'bg-info';
        } else if (status === 'in-progress') {
          progressColor = 'bg-success';
        } else {
          progressColor = 'bg-secondary';
        }
        
        return (
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center fs-13">
              <span className="fw-medium text-dark me-1">Start:</span>
              {getFormattedDateDDMMYYYY(record.startDate)}
            </div>
            <div className="d-flex align-items-center fs-13 mt-1">
              <span className="fw-medium text-dark me-1">End:</span>
              {getFormattedDateDDMMYYYY(record.endDate)}
            </div>
            
            {/* Progress bar showing completion percentage */}
            <div className="progress mt-2" style={{ height: '6px' }}>
              <div 
                className={`progress-bar ${progressColor}`} 
                role="progressbar" 
                style={{ width: `${percentage}%` }} 
                aria-valuenow={percentage} 
                aria-valuemin={0} 
                aria-valuemax={100}
              ></div>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1">
              <p className="fs-12 fw-medium text-dark mb-0">
                Status: 
                {isCurrentDateInBetween(record.startDate,record.endDate)
                  ? <span className='text-success'> Live</span>
                  : isBeforeCurrentDate(record.endDate)
                    ? <span className='text-danger'> Inactive</span>
                    : <span className='text-info'> Upcoming</span>
                }
              </p>
              <span className="fs-12 fw-medium">{percentage}%</span>
            </div>
          </div>
        );
      },
      sorter: (a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      width: "30%",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-flex justify-content-center">
          <Link
            hidden={!hasAcademicMgntEdit() || isBeforeCurrentDate(record.endDate) || (record.status !== 'ACTIVE' && record.status !== 'UPCOMING')}
            to="#"
            className="me-2 icon-edit"
            data-bs-toggle="modal" data-inert={true}
            data-bs-target="#edit_curriculum"
            onClick={() => { setEditId(record?.id) }}
            title="Edit Curriculum"
          >
            <i className="ti ti-edit" />
          </Link>
          <Link
            hidden={!hasAcademicMgntDelete() || isBeforeCurrentDate(record.endDate) || (record.status !== 'ACTIVE' && record.status !== 'UPCOMING')}
            to="#"
            className="icon-delete"
            data-bs-toggle="modal" data-inert={true}
            data-bs-target="#delete_modal"
            onClick={() => setDeleteItem({
              id: record.id,
              message: `Are you sure you want to delete ${record.title}? This can't be undone.`
            })}
            title="Delete Curriculum"
          >
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
      width: "15%",
      align: "center" as "center",
    },
  ]

  const renderAdd = () => {
    return (
      <AddCurriculum onSuccess={fetch}/>
    )
  }

  const renderEdit = () => {
    return (    
      <ModifyCurriculum
        onSuccess={fetch}
        curriculum={curriculums?.find((el:any)=> el.id === editId)}/>
    )
  }


  const renderStatCards = () => {
    return (          
    <div className="row">
      <div className="col-xl-3 col-md-6">
          <div className={`card flex-fill position-relative stats-card ${activeFilter === 'all' ? 'stats-card-active' : ''}`}
               onClick={() => handleFilterClick('all')}>
            <span className="position-absolute start-0 bottom-0">
              <img className="img-fluid" alt="img" src="../../../assets/img/reports-img/total-expense.svg"/>
            </span>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <span className="fs-14 fw-normal text-truncate mb-1">Total Curriculum</span>
                  <h5>{totalCurriculum}</h5>
                </div>
                <span className="avatar avatar-md avatar-rounded bg-transparent-primary border border-primary">
                  <span className="text-primary">
                    <i className="ti ti-book"></i>
                  </span>
                </span>
              </div>
            </div>
          </div>
      </div>
      <div className="col-xl-3 col-md-6">
          <div className={`card flex-fill position-relative stats-card ${activeFilter === 'active' ? 'stats-card-active' : ''}`}
               onClick={() => handleFilterClick('active')}>
            <span className="position-absolute start-0 bottom-0">
              <img className="img-fluid" alt="img" src="../../../assets/img/reports-img/approved-expense.svg"/>
            </span>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <span className="fs-14 fw-normal text-truncate mb-1">Active Curriculum</span>
                  <h5>{activeCurriculum}</h5>
                </div>
                <span className="avatar avatar-md avatar-rounded bg-transparent-success border border-success">
                  <span className="text-success">
                    <i className="ti ti-book"></i>
                  </span>
                </span>
              </div>
            </div>
          </div>
      </div>
      <div className="col-xl-3 col-md-6">
          <div className={`card flex-fill position-relative stats-card ${activeFilter === 'inactive' ? 'stats-card-active' : ''}`}
               onClick={() => handleFilterClick('inactive')}>
            <span className="position-absolute start-0 bottom-0">
              <img className="img-fluid" alt="img" src="../../../assets/img/reports-img/reject-expense.svg"/>
            </span>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <span className="fs-14 fw-normal text-truncate mb-1">Inactive Curriculum</span>
                  <h5>{inactiveCurriculum}</h5>
                </div>
                <span className="avatar avatar-md avatar-rounded bg-transparent-danger border border-danger">
                  <span className="text-danger">
                    <i className="ti ti-book"></i>
                  </span>
                </span>
              </div>
            </div>
          </div>
      </div>
      <div className="col-xl-3 col-md-6">
          <div className={`card flex-fill position-relative stats-card ${activeFilter === 'upcoming' ? 'stats-card-active' : ''}`}
               onClick={() => handleFilterClick('upcoming')}>
            <span className="position-absolute start-0 bottom-0">
              <img className="img-fluid" alt="img" src="../../../assets/img/reports-img/pending-expense.svg"/>
            </span>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <span className="fs-14 fw-normal text-truncate mb-1">Upcoming Curriculum</span>
                  <h5>{upcomingCurriculum}</h5>
                </div>
                <span className="avatar avatar-md avatar-rounded bg-transparent-info border border-info">
                  <span className="text-info">
                    <i className="ti ti-book"></i>
                  </span>
                </span>
              </div>
            </div>
          </div>
      </div>
    </div>)
  }

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <DeleteModal 
              heading="Confirm Delete"
              message={deleteItem.message}
              onDelete={()=>deleteData(deleteItem?.id)}
            />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Curriculum</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Academics</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Curriculum
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="d-flex mb-2 right-content gap-3">
                <button
                  disabled={!hasAcademicMgntCreate()}
                  data-bs-toggle="modal" data-inert={true}
                  data-bs-target="#add_curriculum"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Curriculum
                </button>
                <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={()=>{
                      navigation(`${all_routes.curriculumAcademicWiz}`);
                    }}
                  >
                    <i className="ti ti-settings me-2" />
                    Manage Curriculum
                </button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {renderStatCards()}
          
          {/* Performance Indicator list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Curriculum List</h5>
              <div className="view-toggle">
                <button 
                  className={`btn btn-sm me-2 ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('table')}
                  aria-label="Switch to table view"
                >
                  <i className="ti ti-table me-1"></i> Table View
                </button>
                <button 
                  className={`btn btn-sm ${viewType === 'card' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('card')}
                  aria-label="Switch to card view"
                >
                  <i className="ti ti-layout-grid me-1"></i> Card View
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {viewType === 'table' ? (
                <Table dataSource={filteredCurriculums} columns={columns} Selection={false} refresh={fetch} />
              ) : (
                <div className="curriculum-cards p-3">
                  <div className="row">
                    {filteredCurriculums.map((curriculum: any) => (
                      <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={curriculum.id}>
                        <div className="card h-100 border shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                  <span className="text-primary fw-bold">
                                    {curriculum.title.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <h6 className="mb-0 fw-semibold curriculum-title">
                                  <Link 
                                    to="#" 
                                    className="text-reset text-decoration-none"
                                    onClick={() => { setEditId(curriculum?.id) }}
                                    data-bs-toggle="modal"
                                    data-bs-target="#edit_curriculum"
                                  >
                                    {curriculum.title}
                                  </Link>
                                </h6>
                              </div>
                              <span className="badge badge-soft-primary">{curriculum.code}</span>
                            </div>
                            
                            {curriculum.description && (
                              <p className="fs-12 text-muted mb-3">
                                {curriculum.description.length > 100 
                                  ? curriculum.description.substring(0, 100) + '...' 
                                  : curriculum.description
                                }
                              </p>
                            )}
                            
                            <div className="d-flex justify-content-between mb-3">
                              <div>
                                <div className="fs-12 fw-medium text-dark">Start Date</div>
                                <div className="fs-12">{getFormattedDateDDMMYYYY(curriculum.startDate)}</div>
                              </div>
                              <div>
                                <div className="fs-12 fw-medium text-dark">End Date</div>
                                <div className="fs-12">{getFormattedDateDDMMYYYY(curriculum.endDate)}</div>
                              </div>
                              <div>
                                <div className="fs-12 fw-medium text-dark">Status</div>
                                <div>
                                  {curriculum.status === 'ACTIVE' ? (
                                    <span className="badge badge-soft-success badge-sm">Active</span>
                                  ) : curriculum.status === 'INACTIVE' ? (
                                    <span className="badge badge-soft-danger badge-sm">Inactive</span>
                                  ) : (
                                    <span className="badge badge-soft-info badge-sm">Upcoming</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="curriculum-status fs-12 mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <div className="fw-medium text-dark">Current Status:</div>
                                {isCurrentDateInBetween(curriculum.startDate, curriculum.endDate) ? (
                                  <div className="d-flex align-items-center">
                                    <span className="dot-indicator bg-success me-1"></span>
                                    <span className="text-success">Live</span>
                                  </div>
                                ) : isBeforeCurrentDate(curriculum.endDate) ? (
                                  <div className="d-flex align-items-center">
                                    <span className="dot-indicator bg-danger me-1"></span>
                                    <span className="text-danger">Expired</span>
                                  </div>
                                ) : (
                                  <div className="d-flex align-items-center">
                                    <span className="dot-indicator bg-info me-1"></span>
                                    <span className="text-info">Upcoming</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Progress bar showing curriculum completion */}
                              {(() => {
                                const { percentage, status } = calculateCompletionPercentage(curriculum.startDate, curriculum.endDate);
                                
                                // Determine progress bar color based on status
                                let progressColor = '';
                                if (status === 'upcoming') {
                                  progressColor = 'bg-info';
                                } else if (status === 'in-progress') {
                                  progressColor = 'bg-success';
                                } else {
                                  progressColor = 'bg-secondary';
                                }
                                
                                return (
                                  <>
                                    <div className="progress mt-2" style={{ height: '6px' }}>
                                      <div 
                                        className={`progress-bar ${progressColor}`} 
                                        role="progressbar" 
                                        style={{ width: `${percentage}%` }} 
                                        aria-valuenow={percentage} 
                                        aria-valuemin={0} 
                                        aria-valuemax={100}
                                      ></div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-1">
                                      <span className="fs-12 text-muted">Progress</span>
                                      <span className="fs-12 fw-medium">{percentage}% Complete</span>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                            
                            <div className="d-flex justify-content-end mt-3">
                              <Link
                                hidden={!hasAcademicMgntEdit() || isBeforeCurrentDate(curriculum.endDate) || (curriculum.status !== 'ACTIVE' && curriculum.status !== 'UPCOMING')}
                                to="#"
                                className="btn btn-sm btn-outline-primary me-2"
                                data-bs-toggle="modal" data-inert={true}
                                data-bs-target="#edit_curriculum"
                                onClick={() => { setEditId(curriculum?.id) }}
                                title="Edit Curriculum"
                              >
                                <i className="ti ti-edit me-1" /> Edit
                              </Link>
                              <Link
                                hidden={!hasAcademicMgntDelete() || isBeforeCurrentDate(curriculum.endDate) || (curriculum.status !== 'ACTIVE' && curriculum.status !== 'UPCOMING')}
                                to="#"
                                className="btn btn-sm btn-outline-danger"
                                data-bs-toggle="modal" data-inert={true}
                                data-bs-target="#delete_modal"
                                onClick={() => setDeleteItem({
                                  id: curriculum.id,
                                  message: `Are you sure you want to delete ${curriculum.title}? This can't be undone.`
                                })}
                                title="Delete Curriculum"
                              >
                                <i className="ti ti-trash me-1" /> Delete
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* /Performance Indicator list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo/>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add Department */}
     
      {renderAdd()}
 
      {/* /Add Department */}
      {/* Edit Department */}
      
      {renderEdit()}
    
      {/* /Edit Department */}
    </>


  )
}

export default Curriculum